import { isSurveyHistoryTestModeEnabled } from "common/SurveyHistoryTestMode";
import { createSurveyHistorySeed, loadPreviousSurveySnapshots } from "./surveyHistoryTestData";

export function createCollectionDataService({ apiClient, apiUrl, interpolateURL, logError }) {
  const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);
  const hasValue = (value) => value !== null && value !== undefined && value !== "";
  const sameId = (left, right) => hasValue(left) && hasValue(right) && String(left) === String(right);

  const createFetchedSkeleton = () => ({
    cantonList: [],
    nogaList: [],
    officeSizeList: [],
    selectedCanton: [],
    companyDetail: [],
    companyInput: [],
    companyOverheads: [],
    employees: [],
    employeePayrolls: [],
    employeeAttendanceTimes: [],
    employeesAbsences: [],
    employeeEmplDetails: [],
    employeeNonOrderRelatedAttTime: [],
    survey: null,
    allEmployeeFunctions: [],
    employeeFunctions: [],
    employmentLevels: [],
    employmentStatuses: [],
    previousSurveys: [],
  });

  const baseEndpoints = [
    { stateProperty: "cantonList", endpoint: "canton" },
    { stateProperty: "nogaList", endpoint: "specializations-noga" },
    { stateProperty: "selectedCanton", endpoint: "company", omitLanguage: true },
    { stateProperty: "companyDetail", endpoint: "company-detail", omitLanguage: true },
    { stateProperty: "officeSizeList", endpoint: "company-detail/office-sizes" },
    { stateProperty: "companyInput", endpoint: "company-input", omitLanguage: true },
    { stateProperty: "employees", endpoint: "employee" },
    { stateProperty: "allEmployeeFunctions", endpoint: "employee-functions/noga-code" },
    { stateProperty: "employeePayrolls", endpoint: "employee-payroll" },
    { stateProperty: "employeeAttendanceTimes", endpoint: "employee-attendance-time" },
    { stateProperty: "employeesAbsences", endpoint: "employees-absence" },
    { stateProperty: "employeeEmplDetails", endpoint: "employee-empl-details" },
    { stateProperty: "employeeNonOrderRelatedAttTime", endpoint: "employee-non-order-related-att-time" },
    { stateProperty: "survey", endpoint: "survey/active" },
    { stateProperty: "employmentLevels", endpoint: "employment-level" },
    { stateProperty: "employmentStatuses", endpoint: "employments-status" },
  ];

  async function get(endpoint, { id = null, omitLanguage = false, noCompanyHashHeader = false, companyHash } = {}) {
    let value;
    let failure;
    let result;
    try {
      result = await apiClient({
        method: "GET",
        mode: "resolution",
        url: interpolateURL({ apiUrl, endpoint, id, omitLanguage }),
        hash: companyHash,
        noCompanyHashHeader,
        onSuccess: ({ data }) => {
          value = data;
        },
        onError: (error) => {
          failure = error;
        },
      });
    } catch (error) {
      failure = error;
    }
    if (failure) {
      logError(`${endpoint}${hasValue(id) ? `/${id}` : ""}`, failure);
      throw failure;
    }
    if (value !== undefined) return value;
    if (result?.ok === false) throw result.error || new Error(`/${endpoint} failed`);
    throw new Error(`/${endpoint} completed without a response body`);
  }

  async function fetchIndependent(fetched, companyHash) {
    const entries = await Promise.all(
      baseEndpoints.map(async ({ stateProperty, endpoint, omitLanguage = false }) => [
        stateProperty,
        await get(endpoint, { omitLanguage, companyHash }),
      ])
    );
    entries.forEach(([stateProperty, data]) => {
      fetched[stateProperty] = data ?? (stateProperty === "survey" ? null : []);
    });
  }

  function getActiveRecords(fetched) {
    const survey = fetched.survey || null;
    const companyInput = asArray(fetched.companyInput).find((input) => sameId(input?.surveyId, survey?.id)) || null;
    const companyDetail = asArray(fetched.companyDetail).find((detail) => sameId(detail?.companyInputId, companyInput?.id)) || null;
    return { survey, companyInput, companyDetail };
  }

  async function handleNewCompanyOrNewSurvey(fetched, companyService, companyHash, translate) {
    // new company
    const isNewCompany = fetched.companyDetail.length === 0 && fetched.companyInput.length === 0;
    let defaultNogaCodeObject = fetched.nogaList[0];
    let defaultCantonObject = fetched.cantonList[0];
    let defaultOfficeSizeObject = fetched.officeSizeList[0];
    if (!isNewCompany) {
      // new Survey
      const lastSurveyId = Math.max(...fetched.companyInput.map((i) => i.surveyId));
      const lastCompanyInput = fetched.companyInput.find((e) => e.surveyId === lastSurveyId);
      const lastCompanyDetail = fetched.companyDetail.find((detail) => detail.companyInputId === lastCompanyInput.id);
      const lastOfficeSizeId = lastCompanyDetail?.officeSizeId || 1;
      defaultNogaCodeObject = fetched.nogaList.find((noga) => noga.id === lastCompanyDetail?.nogaId) || fetched.nogaList[0];
      defaultCantonObject = fetched.cantonList.find((canton) => canton.id === fetched.selectedCanton[0].cantonId);
      defaultOfficeSizeObject = fetched.officeSizeList.find((officeSize) => officeSize.id === lastOfficeSizeId);
    }
    let survey = fetched.survey;
    let companyId = fetched?.selectedCanton[0]?.id || null;
    let { data, error } = await companyService.buildNewTemplateModel({
      companyHash,
      requestHash: companyHash,
      nogaCodeObj: defaultNogaCodeObject,
      cantonObj: defaultCantonObject,
      officeSizeObj: defaultOfficeSizeObject,
      survey,
      translate,
      companyId,
    });
    let company = await get("company", { id: null, omitLanguage: true, noCompanyHashHeader: false, companyHash });
    fetched.selectedCanton = company;
    if (error) {
      throw error;
    } else {
      fetched.companyInput = data.companyInput;
      fetched.companyDetail = data.companyDetail;
      fetched.companyOverheads = data.companyOverheads;
    }
  }

  async function fetchDependent(fetched, companyHash, companyService, translate) {
    let { companyInput, companyDetail } = getActiveRecords(fetched);
    let nogaId = null;
    const isNewCompanyOrNewSurvey = !companyInput && !companyDetail;
    if (isNewCompanyOrNewSurvey) {
      await handleNewCompanyOrNewSurvey(fetched, companyService, companyHash, translate);
      fetched.companyOverheads =
        (await get(`company-overhead/company-input/${fetched.companyInput.id}`, { noCompanyHashHeader: true, companyHash })) || [];
      nogaId = fetched.companyDetail.nogaId;
    } else {
      fetched.companyOverheads = (await get(`company-overhead/company-input/${companyInput.id}`, { noCompanyHashHeader: true, companyHash })) || [];
      nogaId = companyDetail.nogaId;
    }

    if (!hasValue(nogaId)) return;
    let specialization = asArray(fetched.nogaList).find((item) => sameId(item?.id, nogaId)) || null;
    if (!hasValue(specialization?.nogaCode)) {
      specialization = await get("specializations-noga", { id: nogaId, noCompanyHashHeader: true, companyHash });
      if (Array.isArray(specialization)) specialization = specialization[0] || null;
    }
    if (!hasValue(specialization?.nogaCode)) throw new Error(`Cannot load employee functions: missing nogaCode for nogaId=${nogaId}`);
    fetched.employeeFunctions =
      (await get("employee-functions/noga-code", {
        id: specialization.nogaCode,
        noCompanyHashHeader: true,
        companyHash,
      })) || [];
  }

  function overrideEmployeesData(fetched) {
    let { companyInput } = getActiveRecords(fetched);
    let currentInputId = companyInput.id;
    let attendances = fetched.employeeAttendanceTimes.filter((e) => e.companyInputId === currentInputId);
    let details = fetched.employeeEmplDetails.filter((e) => e.companyInputId === currentInputId);
    let nonOrderRats = fetched.employeeNonOrderRelatedAttTime.filter((e) => e.companyInputId === currentInputId);
    let payRolls = fetched.employeePayrolls.filter((e) => e.companyInputId === currentInputId);
    let absences = fetched.employeesAbsences.filter((e) => e.companyInputId === currentInputId);
    const shouldOverride = [attendances, details, nonOrderRats, payRolls, absences].every((e) => e.length === 0);
    if (shouldOverride) {
      fetched.employees = [];
      fetched.employeeAttendanceTimes = [];
      fetched.employeeEmplDetails = [];
      fetched.employeeNonOrderRelatedAttTime = [];
      fetched.employeePayrolls = [];
      fetched.employeesAbsences = [];
    } else {
      let currentEmployeeIds = attendances.map((e) => e.employeeId);
      fetched.employees = fetched.employees.filter((emp) => currentEmployeeIds.includes(emp.id));
      fetched.employeeAttendanceTimes = attendances;
      fetched.employeeEmplDetails = details;
      fetched.employeeNonOrderRelatedAttTime = nonOrderRats;
      fetched.employeePayrolls = payRolls;
      fetched.employeesAbsences = absences;
    }
    return fetched;
  }

  async function fetchData({ companyHash, companyService, translate }) {
    if (!companyHash) throw new Error("A company hash is required to load Survey data");
    const fetched = createFetchedSkeleton();
    await fetchIndependent(fetched, companyHash);
    const testMode = isSurveyHistoryTestModeEnabled();
    const history = testMode ? createSurveyHistorySeed(fetched) : null;
    await fetchDependent(fetched, companyHash, companyService, translate);
    if (history) {
      fetched.previousSurveys = await loadPreviousSurveySnapshots({
        history,
        activeSurveyId: fetched.survey?.id,
        loadOverheads: (companyInputId) =>
          get(`company-overhead/company-input/${companyInputId}`, {
            noCompanyHashHeader: true,
            companyHash,
          }),
      });
    }
    overrideEmployeesData(fetched);
    return fetched;
  }

  async function fetchEmployeeFunctionsForNoga({ nogaId }) {
    if (!hasValue(nogaId)) return [];
    let specialization = await get("specializations-noga", { id: nogaId, noCompanyHashHeader: true });
    if (Array.isArray(specialization)) specialization = specialization[0] || null;
    if (!hasValue(specialization?.nogaCode)) return [];
    return (await get("employee-functions/noga-code", { id: specialization.nogaCode, noCompanyHashHeader: true })) || [];
  }

  return { fetchData, fetchEmployeeFunctionsForNoga };
}
